import { Eye, MoreVertical, Pencil, type LucideIcon } from 'lucide-react';

export interface RowActionsProps {
  /** Names the record, for the title and the screen reader — "TS09 UB 1234". */
  subject: string;
  onOpen?: () => void;
  onEdit?: () => void;
  onMore?: () => void;
}

/**
 * The view / edit / more trio at the end of every register row.
 *
 * One component so the three icons cannot end up in a different order on one
 * tab, and so each carries a label naming *which* record it acts on — a table
 * of twenty rows with twenty buttons all announced as "Edit" tells a screen
 * reader user nothing (CLAUDE.md §7).
 */
export function RowActions({ subject, onOpen, onEdit, onMore }: RowActionsProps) {
  return (
    <div className="flex items-center justify-end gap-1">
      {onOpen && <RowAction icon={Eye} label={`Open ${subject}`} onSelect={onOpen} />}
      {onEdit && <RowAction icon={Pencil} label={`Edit ${subject}`} onSelect={onEdit} />}
      {onMore && <RowAction icon={MoreVertical} label={`More for ${subject}`} onSelect={onMore} />}
    </div>
  );
}

function RowAction({
  icon: Icon,
  label,
  onSelect,
}: {
  icon: LucideIcon;
  label: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      title={label}
      aria-label={label}
      className="grid h-8 w-8 place-items-center rounded-xl text-ink-3 transition-colors hover:bg-surface-2 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
